import React, { useState, useEffect } from 'react';
import { scGet } from '@/lib/scFetch';
import { toast } from 'react-hot-toast';

interface AuditRow {
  id: number;
  action: string;
  discord_id: string;
  username: string;
  client_ip: string;
  detail: string;
  path: string;
  timestamp: string;
}

export function AuditPage() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAuditLog = async () => {
    setLoading(true);
    try {
      const data = await scGet<{ rows: AuditRow[] }>('/fivem/audit');
      setRows(data.rows || []);
    } catch (err: any) {
      toast.error('Failed to load audit logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
  }, []);

  const getActionColor = (action: string) => {
    const act = action.toUpperCase();
    if (act.includes('FAIL')) return 'text-red-400 bg-red-400/10 border-red-500/20';
    if (act.includes('SUCCESS') || act.includes('UPLOAD')) return 'text-[#00ff66] bg-cyberGreen/10 border-cyberGreen/20';
    if (act.includes('DELETE')) return 'text-red-400 bg-red-400/10 border-red-500/20';
    if (act.includes('EDIT')) return 'text-[#00ffd1] bg-[#00ffd1]/10 border-[#00ffd1]/20';
    return 'text-slate-400 bg-white/5 border-white/10';
  };

  const filteredRows = rows.filter((r) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      (r.username || '').toLowerCase().includes(query) ||
      (r.discord_id || '').toLowerCase().includes(query) ||
      (r.action || '').toLowerCase().includes(query) ||
      (r.detail || '').toLowerCase().includes(query) ||
      (r.path || '').toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold font-display uppercase tracking-wider text-white">Audit Trail</h2>
          <p className="text-xs text-slate-400">Review security records and track operations performed in this panel.</p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search audit trail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-white/[0.02] border border-white/10 rounded-xl px-4 py-2 text-slate-100 placeholder:text-slate-600 text-xs font-mono focus:outline-none focus:border-[#00ffd1]/50 w-64"
          />
          <button
            onClick={fetchAuditLog}
            disabled={loading}
            className="px-4 py-2 bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 rounded-xl text-slate-300 hover:text-white text-xs transition-all flex items-center gap-1.5"
          >
            <i className={`fa-solid fa-arrows-rotate ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="glass-card">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyberCyan/10 to-transparent" />
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
              <tr>
                <th className="px-5 py-3.5">Timestamp</th>
                <th className="px-5 py-3.5">Operator</th>
                <th className="px-5 py-3.5">Action</th>
                <th className="px-5 py-3.5">Path / Target</th>
                <th className="px-5 py-3.5">IP Address</th>
                <th className="px-5 py-3.5">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04] text-slate-200">
              {filteredRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-slate-500">
                    No matching audit records found.
                  </td>
                </tr>
              ) : null}

              {filteredRows.map((row) => (
                <tr key={row.id} className="hover:bg-white/[0.01] transition-colors">
                  <td className="px-5 py-3.5 text-slate-500 font-mono">
                    {row.timestamp ? new Date(row.timestamp).toLocaleString() : 'unknown'}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-200">{row.username || 'unknown'}</span>
                      <span className="text-[10px] text-slate-500">{row.discord_id}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold border ${getActionColor(row.action)}`}>
                      {row.action}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400 font-mono max-w-[150px] truncate" title={row.path}>
                    {row.path || '-'}
                  </td>
                  <td className="px-5 py-3.5 text-slate-500 font-mono">{row.client_ip}</td>
                  <td className="px-5 py-3.5 text-slate-400 font-sans max-w-[200px] truncate" title={row.detail}>
                    {row.detail || '-'}
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-[#00ffd1] font-bold">
                    <i className="fa-solid fa-spinner animate-spin mr-2" /> Syncing audit trail...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
export default AuditPage;
